import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { Observable, withLatestFrom } from 'rxjs';
import { MessageService } from 'primeng/api';
import { PaymentMethod, ReorderDetails } from 'src/app/core/models';
import {
  CreateToDo,
  MakePayment,
  SetCollectionStatusIfNeeded,
  SubmitReorder,
} from 'src/app/core/store/order-wizard/order-wizard-submission/order-wizard-submission.actions';
import { OrderWizardDataState } from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.state';
import {
  OrderWizardStatus,
  OutOfPocketMetStatus,
} from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.model';
import { ApiStoreData, CcsSentryError } from 'src/app/core/store/utils';
import { Gtag } from 'angular-gtag';
import { OrderWizardSubmissionState } from 'src/app/core/store/order-wizard/order-wizard-submission/order-wizard-submission.state';

@Component({
  selector: 'app-wizard-order-summary',
  templateUrl: './wizard-order-summary.component.html',
  styleUrls: ['./wizard-order-summary.component.scss'],
})
export class WizardOrderSummaryComponent {
  @Input() isConfirmationTouched: boolean = false;
  @Input() useForSubmission: boolean = false;
  @Input() confirmDeductibleOverrides: boolean = false;
  @Output() confirmDeductibleOverridesChange = new EventEmitter<boolean>();

  @Select(OrderWizardDataState.paymentOptional) paymentOptional$!: Observable<boolean>;
  @Select(OrderWizardDataState.hideSummaryStepBillingInfo) hideSummaryStepBillingInfo$!: Observable<OrderWizardStatus>;
  @Select(OrderWizardDataState.skipCollectionForReconsent) skipCollectionForReconsent$!: Observable<boolean>;
  @Select(OrderWizardDataState.skipBillingStep) skipBillingStep$!: Observable<OrderWizardStatus>;
  @Select(OrderWizardSubmissionState.submitReorder) submitReorder$!: Observable<ApiStoreData<null>>;
  @Select(OrderWizardSubmissionState.makePayment) makePayment$!: Observable<ApiStoreData<null>>;
  @Select(OrderWizardDataState.selectedPaymentMethod) selectedPaymentMethod$!: Observable<PaymentMethod | null>;
  @Select(OrderWizardDataState.requireOrderConfirmation) requireOrderConfirmation$!: Observable<boolean>;
  @Select(OrderWizardDataState.updateCopayDeductibleOverridesState) updateCopayDeductibleOverridesState$!: Observable<
    ApiStoreData<null>
  >;
  @Select(OrderWizardDataState.outOfPocket) selectedOutOfPocket$!: Observable<OutOfPocketMetStatus>;

  displayOrderSubmissionError: boolean = false;
  displayPaymentError: boolean = false;
  isSubmittingReorder: boolean = false;

  constructor(
    private store: Store,
    private router: Router,
    private messageService: MessageService,
    private gtag: Gtag
  ) {}

  submitReorderAndRelatedUpdates(makePayment: boolean, makePaymentError?: CcsSentryError | null) {
    const isPaymentMade = makePayment && !makePaymentError;
    this.store.dispatch(new SetCollectionStatusIfNeeded(isPaymentMade));

    this.store
      .dispatch(new SubmitReorder(!makePayment))
      .pipe(withLatestFrom(this.submitReorder$))
      .subscribe(([_, submitReorder]) => {
        this.isSubmittingReorder = false;

        const sendToDo = this.store.selectSnapshot(OrderWizardSubmissionState.sendToDo);

        if (!submitReorder.error) {
          if (sendToDo?.value) {
            this.store.dispatch(new CreateToDo());
          }

          const reorderId = this.store.selectSnapshot(OrderWizardDataState.reorder)?.id;

          this.router.navigate(['/'], {
            queryParams: { checkAddressChanges: true, excludeAddressChangeReorderId: reorderId },
          });

          if (!makePaymentError) {
            this.messageService.add({
              key: 'homeToast',
              severity: 'success',
              detail: 'Thank you for your order, it has been submitted successfully.',
              sticky: true,
            });
          } else {
            this.messageService.add({
              key: 'homeToast',
              severity: 'info',
              detail:
                'Thank you for your order, it has been submitted successfully. Could not collect payment at this time.',
              sticky: true,
            });
          }

          this.gtag.event('order_submission_success', {
            event_category: 'engagement',
            non_interaction: true,
          });
        } else {
          this.showOrderSubmissionError();

          this.gtag.event('order_submission_failure', {
            event_category: 'engagement',
            non_interaction: true,
            error: submitReorder.error.uiMessage,
          });
        }
      });
  }

  onSubmit(makePayment: boolean = true) {
    this.isConfirmationTouched = true;

    const skipBillingStep = this.store.selectSnapshot(OrderWizardDataState.skipBillingStep);
    const requireOrderConfirmation = this.store.selectSnapshot(OrderWizardDataState.requireOrderConfirmation);
    const isOrderConfirmed = this.store.selectSnapshot(OrderWizardDataState.isOrderConfirmed);

    if (requireOrderConfirmation && skipBillingStep?.value && !isOrderConfirmed) {
      return;
    }

    if ((skipBillingStep && skipBillingStep?.value) || !makePayment) {
      this.isSubmittingReorder = true;
      this.submitReorderAndRelatedUpdates(makePayment);
      return;
    }

    const paymentMethodSelected = this.store.selectSnapshot(OrderWizardDataState.selectedPaymentMethod);
    if (!paymentMethodSelected) {
      return;
    }

    this.isSubmittingReorder = true;

    this.store
      .dispatch(new MakePayment())
      .pipe(withLatestFrom(this.makePayment$))
      .subscribe(([_, makePaymentState]) => {
        // TODO Restore error on payment failures when payment API stabilizes
        // if (makePaymentState.error) {
        //   this.showPaymentError();
        //   return;
        // }

        // this.submitReorderAndRelatedUpdates(makePayment, makePayment.error);
        this.submitReorderAndRelatedUpdates(makePayment, makePaymentState.error);
      });
  }

  onConfirmDeductibleOverridesChange(event: boolean) {
    this.confirmDeductibleOverridesChange.emit(event);
  }

  showOrderSubmissionError() {
    this.displayOrderSubmissionError = true;
  }

  hideOrderSubmissionError() {
    this.displayOrderSubmissionError = false;
  }

  showPaymentError() {
    this.displayPaymentError = true;
  }

  hidePaymentError() {
    this.displayPaymentError = false;
  }
}
